import {createContext, useContext} from 'react';
import type {GroupConfig} from '@client/pages/public/enroll/types';
import {absoluteHref} from '@common/enrollmentSite/siteLinks';

// Preview mode for the enroll flow: when isPreview is set, the flow renders read-only
// from an injected draft config — gate skippable, submission stubbed. Off by default.
export interface EnrollPreviewState {
  isPreview: boolean;
  /** Published demo/sales site: reuses preview's gate-skip + stubbed submit, but public and interactive. */
  isDemo?: boolean;
  /** Injected group config — supplied instead of fetching the published one. */
  config?: GroupConfig;
  /** Formatted "last saved" time of the draft, shown in the preview banner. */
  savedAt?: string;
  /** True briefly after a newer save arrives — the banner shows a refreshing note. */
  refreshing?: boolean;
  /** Public site origin; the preview is served from the internal portal, which lacks the site's files. */
  siteOrigin?: string;
}

const EnrollPreviewContext = createContext<EnrollPreviewState>({isPreview: false});

export const EnrollPreviewProvider = EnrollPreviewContext.Provider;

export function useEnrollPreview(): EnrollPreviewState {
  return useContext(EnrollPreviewContext);
}

/** Resolves a config link for an `<a>`: site-relative files point at the public site in preview. */
export function useLinkHref(): (href: string) => string {
  const {isPreview, siteOrigin} = useEnrollPreview();
  return (href) => {
    const abs = absoluteHref(href);
    return isPreview && siteOrigin && /^\/(?!\/)/.test(abs) ? `${siteOrigin}${abs}` : abs;
  };
}

/** Same-origin channel the builder pings on save/publish so open previews live-reload. */
export const ENROLL_PREVIEW_CHANNEL = 'enroll-preview';

export interface EnrollPreviewMessage {
  type: 'saved';
  slug: string;
  at: number;
}

/** Ping every open preview of `slug` to refetch. No-op where BroadcastChannel is unsupported. */
export function broadcastPreviewSaved(slug: string): void {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return;
  const channel = new BroadcastChannel(ENROLL_PREVIEW_CHANNEL);
  channel.postMessage({type: 'saved', slug, at: Date.now()} satisfies EnrollPreviewMessage);
  channel.close();
}
